import { create } from 'zustand';
import { OverviewService, type OverviewResponse } from '../lib/services/analytics/overviewService';
import { analyticsApi, type DashboardStat } from '../lib/services/analytics/analyticsApi';
import { getOverviewDimensionFromPeriod, calculateTimeSavedByAI } from '../lib/utils/dateUtils';

interface OverviewState {
  // Raw API data
  dashboardData: OverviewResponse | null;

  // Processed stats for StatsGrid
  dashboardStats: DashboardStat[];
  conversationCount: number;
  timeSavedChange: number;
  timeSaved: string;

  // Chart data for StoreTraffic
  chartData: Array<{
    date: string;
    newLeads: number;
    newConversations: number;
  }>;

  // Trends
  trends: {
    conversation: string;
    lead: string;
  };

  // Loading and error states
  isLoading: boolean;
  error: string | null;

  // Current fetch params (for deduplication)
  currentUserId: string | null;
  currentPeriod: string | null;

  // Actions
  fetchDashboardData: (userId: string, selectedPeriod: string) => Promise<void>;
  reset: () => void;
}

const initialState = {
  dashboardData: null,
  dashboardStats: [],
  conversationCount: 0,
  timeSavedChange: 0,
  timeSaved: '0 min',
  chartData: [],
  trends: {
    conversation: '0',
    lead: '0',
  },
  isLoading: false,
  error: null,
  currentUserId: null,
  currentPeriod: null,
};

export const useOverviewStore = create<OverviewState>((set, get) => ({
  ...initialState,

  fetchDashboardData: async (userId: string, selectedPeriod: string) => {
    const state = get();

    // Skip if already loading the same data
    if (
      state.isLoading &&
      state.currentUserId === userId &&
      state.currentPeriod === selectedPeriod
    ) {
      return;
    }

    // Skip if data is already loaded for the same params
    if (
      state.dashboardData &&
      state.currentUserId === userId &&
      state.currentPeriod === selectedPeriod &&
      !state.error
    ) {
      return;
    }

    set({
      isLoading: true,
      error: null,
      currentUserId: userId,
      currentPeriod: selectedPeriod,
    });

    try {
      // Convert period to dimension
      const dimension = getOverviewDimensionFromPeriod(selectedPeriod);

      // Generate endDateTime in UTC (local 23:59:59 converted to UTC)
      // e.g., Beijing (UTC+8) 23:59:59 → UTC 15:59:59
      const now = new Date();
      // For "yesterday" dimension, use yesterday's date
      if (dimension === 'yesterday') {
        now.setDate(now.getDate() - 1);
      }
      // Set local time to 23:59:59
      now.setHours(23, 59, 59, 0);
      // Convert to UTC
      const utcDate = new Date(now.getTime());
      const day = String(utcDate.getUTCDate()).padStart(2, '0');
      const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
      const year = utcDate.getUTCFullYear();
      const hours = String(utcDate.getUTCHours()).padStart(2, '0');
      const minutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
      const seconds = String(utcDate.getUTCSeconds()).padStart(2, '0');
      const endDateTime = `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;

      const data = await OverviewService.getDashboardData(userId, dimension, endDateTime);

      // Process trend data into dashboard stats
      const trendList = data.trendList || [];
      const processedStats = analyticsApi.processTrendListToStats(trendList);

      // Get conversation count for time saved calculation
      const conversationStat = processedStats.find(stat =>
        stat.title.toLowerCase().includes('conversation')
      );
      const count = conversationStat
        ? typeof conversationStat.value === 'string'
          ? parseInt(conversationStat.value.replace(/,/g, ''), 10) || 0
          : conversationStat.value
        : 0;

      // Calculate time saved change
      const changeRate = conversationStat
        ? typeof conversationStat.changeRate === 'string'
          ? parseFloat(conversationStat.changeRate) || 0
          : conversationStat.changeRate
        : 0;
      const timeSavedChange = conversationStat?.isPositiveChange
        ? Math.abs(changeRate)
        : -Math.abs(changeRate);

      // Transform data for chart
      const chartData = OverviewService.transformChartData(data.dataStatisticList);

      // Get trends
      const conversationTrend = data.trendList.find(t => t.type === 'conversation');
      const leadTrend = data.trendList.find(t => t.type === 'lead');

      set({
        dashboardData: data,
        dashboardStats: processedStats,
        conversationCount: count,
        timeSavedChange,
        timeSaved: calculateTimeSavedByAI(count),
        chartData,
        trends: {
          conversation: conversationTrend?.rate || '0',
          lead: leadTrend?.rate || '0',
        },
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);

      // Set fallback data on error
      set({
        dashboardData: null,
        dashboardStats: [
          {
            title: 'Total Conversations',
            value: '0',
            changeRate: '0',
            isPositiveChange: true,
            period: 'last week',
          },
          {
            title: 'Total Leads',
            value: '0',
            changeRate: '0',
            isPositiveChange: true,
            period: 'last week',
          },
          {
            title: 'Online Store Visitors',
            value: '0',
            changeRate: '0',
            isPositiveChange: true,
            period: 'last week',
          },
        ],
        conversationCount: 0,
        timeSavedChange: 0,
        timeSaved: '0 min',
        chartData: [],
        trends: {
          conversation: '0',
          lead: '0',
        },
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch dashboard data',
      });
    }
  },

  reset: () => {
    set(initialState);
  },
}));
